import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Spin, message } from 'antd';
import BottomNav from '../../components/BottomNav';
import './index.less';

interface Achievement {
  id: number;
  achievementName: string;
  title: string;
  description: string;
  imageUrl: string;
}

interface AddressData {
  hash: string;
  achievements: Achievement[];
}

interface Position {
  x: number;
  y: number;
  width: number;
  height: number;
}

const AchievementsPage: React.FC = () => {
  const { hash } = useParams<{ hash: string }>();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AddressData | null>(null);
  const [expandedCard, setExpandedCard] = useState<Achievement | null>(null);
  const [cardPosition, setCardPosition] = useState<Position | null>(null);
  const cardRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  useEffect(() => {
    fetchData();
  }, [hash]);

  const fetchData = async () => {
    try {
      const response = await fetch(`/api/v1/backroomsteamcard/address/${hash}`);
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      const result = await response.json();
      setData(result);
    } catch (error) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const calculatePosition = (element: HTMLDivElement): Position => {
    const rect = element.getBoundingClientRect();
    const centerX = window.innerWidth / 2;
    const targetY = window.innerHeight * 0.3;
    const scale = 1.5;

    // 计算缩放后的尺寸
    const scaledWidth = rect.width * scale;
    const scaledHeight = rect.height * scale;

    // 计算偏移量，考虑缩放后的尺寸
    const offsetX = (scaledWidth - rect.width) / 2;
    const offsetY = (scaledHeight - rect.height) / 2;

    // 添加修正值，确保完全居中
    const correctionX = 16; // 向右偏移的修正值

    return {
      x: centerX - rect.left - rect.width / 2 - offsetX - correctionX,
      y: targetY - rect.top - rect.height / 2 - offsetY,
      width: rect.width,
      height: rect.height
    };
  };

  const handleCardClick = (achievement: Achievement) => {
    const cardElement = cardRefs.current[achievement.id];
    if (cardElement) {
      const position = calculatePosition(cardElement);
      setCardPosition(position);
      setExpandedCard(achievement);
    }
  };

  const handleClose = () => {
    setExpandedCard(null);
    setCardPosition(null);
  };

  if (loading) {
    return (
      <div className="achievements-page loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="achievements-page">
      <div className="content">
        <div className="archievement-title">我的成就</div>
        <div className="achievements-grid">
          {data?.achievements && data.achievements.length > 0 ? (
            data.achievements.map((achievement) => (
              <div
                key={achievement.id}
                ref={el => cardRefs.current[achievement.id] = el}
                className={`achievement-card ${expandedCard?.id === achievement.id ? 'expanded' : ''}`}
                onClick={() => handleCardClick(achievement)}
                style={expandedCard?.id === achievement.id && cardPosition ? {
                  transform: `translate(${cardPosition.x}px, ${cardPosition.y}px) scale(1.5) rotateY(360deg)`,
                  zIndex: 1001, // 确保在遮罩层之上
                } : undefined}
              >
                <img src={'https://oss.usecubes.cn/backrooms_team/archievements/' + achievement.id + '.png'} alt={achievement.achievementName} />
                <div className="achievement-info">
                  <h2>{achievement.achievementName}</h2>
                  <p>{achievement.description}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <p className="empty-title">还未获得成就</p>
              <p className="empty-desc">前往事件大厅参与新事件获取成就吧！</p>
            </div>
          )}
        </div>
      </div>

      {expandedCard && (
        <div className="achievement-modal" onClick={handleClose} style={{ zIndex: 1000 }} />
      )}

      <BottomNav hash={hash} />
    </div>
  );
};

export default AchievementsPage; 